import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, X, Gift, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginModal = ({
  isOpen,
  onClose,
  onLogin,
  currentUser
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // Carrega dados de indicação da loja atual (só faz sentido se já estiver logado)
    if (currentUser) {
      axios.get(`${API}/referrals/me`).then((r) => setReferral(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [isOpen, currentUser]);

  if (!isOpen) return null;

  const handleCustomLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Informe e-mail e senha.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onLogin(email.trim(), password.trim());
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha na autenticação.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyReferral = async () => {
    if (!referral?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referral.referral_link);
      setCopied(true);
      toast.success("Link de indicação copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
      <div
        data-testid="modal-login-dialog"
        className="w-full max-w-xl bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl shadow-[0_0_60px_rgba(212,175,55,0.3)] overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              Autenticação & Troca de Perfil Maçônico
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="btn-close-login-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5 text-xs text-slate-300">
          {/* Custom Login Form */}
          <form onSubmit={handleCustomLogin} className="space-y-3">
            <div>
              <label className="block text-slate-300 font-semibold mb-1">E-mail ou CIM do Irmão</label>
              <input
                data-testid="input-login-email"
                type="text"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@lojamaconica.org  ou  62188"
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37] text-xs font-mono"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-slate-300 font-semibold mb-1">Senha de Acesso</label>
              <input
                data-testid="input-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37] text-xs font-mono"
              />
            </div>

            <button
              data-testid="btn-submit-login"
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)] mt-2"
            >
              {isSubmitting ? "Autenticando..." : "Entrar no Sistema"}
            </button>
          </form>

          {/* Bloco de Indicação — só aparece se estiver logado e o backend devolver o link */}
          {currentUser && referral?.referral_link && (
            <div
              data-testid="referral-invite-block"
              className="mt-2 p-4 rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-950/40 via-[#0B1220] to-[#0B1220]"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
                  <Gift className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold block">
                    Indique uma Loja e ganhe bônus
                  </span>
                  <p className="text-[11px] text-slate-300 mt-0.5 leading-snug">
                    Ao cada Loja cadastrada com o seu link, sua Loja ganha <b className="text-emerald-300">+1 mês grátis</b>.
                    {typeof referral.free_months_balance === "number" && (
                      <> Saldo atual: <b className="text-emerald-300 font-mono">{referral.free_months_balance} {referral.free_months_balance === 1 ? "mês" : "meses"}</b>.</>
                    )}
                  </p>
                  <div className="mt-2 flex items-stretch gap-2">
                    <input
                      readOnly
                      value={referral.referral_link}
                      data-testid="referral-link-input"
                      className="flex-1 min-w-0 bg-[#080C14] text-emerald-300 p-2 rounded-lg border border-slate-800 text-[11px] font-mono truncate"
                    />
                    <button
                      type="button"
                      onClick={handleCopyReferral}
                      data-testid="btn-copy-referral"
                      className="flex items-center gap-1 px-3 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[10px] uppercase tracking-wider font-bold transition-all"
                    >
                      {copied ? <><Check className="w-3.5 h-3.5" /> Copiado</> : <><Copy className="w-3.5 h-3.5" /> Copiar</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
